import {
  X,
  Smartphone,
  User,
  DollarSign,
  Clock,
  Trash2,
  AlertTriangle,
  Pencil,
  Save,
  CalendarRange,
  Home,
} from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useEffect, useState } from "react"
import CollectPaymentModal from "./CollectPaymentModal"
import { BASE_URL } from "../../api/api"
import { useSelector } from "react-redux"
import {
  deleteGeneralSale,
  deletePhoneSale,
  updateGeneralProduct,
  updateGeneralSale,
  updatePhoneSale,
} from "../../api/products/post"
import { notifyDashboardRefresh } from "../../utils/dashboardRefresh"
import toast from "react-hot-toast"
import { getStoreExperience } from "../../utils/storeExperience"
import { isPropertyStore } from "../../utils/storeCategory"

type Props = {
  isOpen: any
  storeId: any
  sale: any | null
  onClose: () => void
  onUpdate: (updatedSale: any) => void
  onDeleted?: () => void
}

const ensureMinimumDelay = async (promise: Promise<any>, minimumMs = 2000) => {
  const [result] = await Promise.all([
    promise,
    new Promise((resolve) => setTimeout(resolve, minimumMs)),
  ])

  return result
}

export default function SaleDetailsModal({
  isOpen = false,
  storeId,
  sale,
  onClose,
  onUpdate,
  onDeleted,
}: Props) {
  const { userdata } = useSelector((state: any) => state.UserReducer)
  const isAdmin = userdata?.role === "ADMIN"
  const experience = getStoreExperience(userdata?.store)
  const propertyStore = isPropertyStore(userdata?.store)
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [clientName, setClientName] = useState("")
  const [clientPhone, setClientPhone] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("CASH")
  const [unitSellPrice, setUnitSellPrice] = useState("")
  const [quantity, setQuantity] = useState("1")
  const [phoneSellPrice, setPhoneSellPrice] = useState("")
  const [transactionType, setTransactionType] = useState("SALE")
  const [stayDays, setStayDays] = useState("")
  const [rentalStartDate, setRentalStartDate] = useState("")
  const [rentalEndDate, setRentalEndDate] = useState("")
  const [availability, setAvailability] = useState("OUI")

  const isGeneralSale = !!sale?.productId || !!sale?.productName || !!sale?.identifier
  const propertyTransaction = propertyStore && isGeneralSale
  const productName = sale?.productId?.name || sale?.productName || "Produit"
  const productRef = sale?.productId?.identifier || sale?.identifier || "-"
  const productBrand = sale?.productId?.brand || ""
  const paidAmount = Number(sale?.paid || 0)
  const effectiveQuantity = Math.max(1, Number(quantity || sale?.quantity || 1))
  const effectiveUnitPrice = Number(unitSellPrice || sale?.unitSellPrice || 0)
  const nextGeneralTotal = effectiveQuantity * effectiveUnitPrice
  const nextPhoneTotal = Number(phoneSellPrice || sale?.sellPrice || 0)
  const previewTotal = isGeneralSale ? nextGeneralTotal : nextPhoneTotal
  const previewRemaining = Math.max(0, previewTotal - paidAmount)

  useEffect(() => {
    if (!sale) return

    setEditing(false)
    setSaving(false)
    setDeleting(false)
    setConfirmDeleteOpen(false)
    setClientName(String(sale.clientName || ""))
    setClientPhone(String(sale.clientPhone || ""))
    setPaymentMethod(String(sale.paymentMethod || "CASH"))
    setUnitSellPrice(
      String(
        sale.unitSellPrice ??
          (sale.sellPrice && sale.quantity ? sale.sellPrice / sale.quantity : 0) ??
          ""
      )
    )
    setQuantity(String(sale.quantity || 1))
    setPhoneSellPrice(String(sale.sellPrice || ""))
    setTransactionType(String(sale.transactionType || sale.listingType || "SALE"))
    setStayDays(String(sale.stayDays || sale.rentalDays || ""))
    setRentalStartDate(String(sale.rentalStartDate || sale.rentStartDate || sale.startDate || ""))
    setRentalEndDate(String(sale.rentalEndDate || sale.rentEndDate || sale.endDate || ""))
    setAvailability(String(sale.availability || "OUI"))
  }, [sale])

  if (!sale || !isOpen) return null

  const heroClass =
    experience.kind === "property"
      ? "from-emerald-900 via-teal-700 to-cyan-700"
      : experience.kind === "car"
        ? "from-slate-950 via-slate-900 to-orange-700"
        : "from-blue-700 via-blue-600 to-cyan-600"

  const accentButtonClass =
    experience.kind === "property"
      ? "bg-emerald-600 hover:bg-emerald-700"
      : experience.kind === "car"
        ? "bg-orange-500 hover:bg-orange-600"
        : "bg-blue-600 hover:bg-blue-700"

  const handleSave = async () => {
    if (!storeId || !sale?._id) return

    try {
      if (!clientName.trim()) {
        toast.error("Nom du client requis")
        return
      }

      if (
        propertyTransaction &&
        resolveTransactionType(transactionType) === "location" &&
        (!rentalStartDate || !rentalEndDate)
      ) {
        toast.error("La periode de location est requise")
        return
      }

      setSaving(true)

      const response = await ensureMinimumDelay(
        isGeneralSale
          ? updateGeneralSale(storeId, sale._id, {
              quantity: Number(quantity || sale.quantity || 1),
              unitSellPrice: Number(unitSellPrice || sale.unitSellPrice || 0),
              clientName: clientName.trim(),
              clientPhone: clientPhone.trim(),
              paymentMethod,
              payOnline: !!sale.payOnline,
              transactionType,
              stayDays: stayDays ? Number(stayDays) : undefined,
              rentalDays: stayDays ? Number(stayDays) : undefined,
              rentalStartDate: rentalStartDate || undefined,
              rentalEndDate: rentalEndDate || undefined,
              availability,
            })
          : updatePhoneSale(sale._id, storeId, {
              sellPrice: Number(phoneSellPrice || sale.sellPrice || 0),
              clientName: clientName.trim(),
              clientPhone: clientPhone.trim(),
              paymentMethod,
            })
      )

      if (propertyTransaction && sale?.productId?._id && availability) {
        try {
          await updateGeneralProduct(storeId, sale.productId._id, { availability })
        } catch (availabilityError) {
          console.warn("Unable to sync property availability", availabilityError)
        }
      }

      toast.success(propertyTransaction ? "Transaction modifiee" : "Vente modifiee")
      notifyDashboardRefresh()
      setEditing(false)
      onUpdate(response)
    } catch (error: any) {
      toast.error(error?.message || "Erreur lors de la modification")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!storeId || !sale?._id || !isAdmin) return

    try {
      setDeleting(true)
      const response = await ensureMinimumDelay(
        isGeneralSale
          ? deleteGeneralSale(storeId, sale._id)
          : deletePhoneSale(sale._id, storeId)
      )

      toast.success(response?.message || "Vente supprimee")
      notifyDashboardRefresh()
      setConfirmDeleteOpen(false)
      onDeleted?.()
      onClose()
    } catch (error: any) {
      toast.error(error?.message || "Erreur lors de la suppression")
    } finally {
      setDeleting(false)
    }
  }

  const resetEditingState = () => {
    setEditing(false)
    setClientName(String(sale.clientName || ""))
    setClientPhone(String(sale.clientPhone || ""))
    setPaymentMethod(String(sale.paymentMethod || "CASH"))
    setUnitSellPrice(
      String(
        sale.unitSellPrice ??
          (sale.sellPrice && sale.quantity ? sale.sellPrice / sale.quantity : 0) ??
          ""
      )
    )
    setQuantity(String(sale.quantity || 1))
    setPhoneSellPrice(String(sale.sellPrice || ""))
    setTransactionType(String(sale.transactionType || sale.listingType || "SALE"))
    setStayDays(String(sale.stayDays || sale.rentalDays || ""))
    setRentalStartDate(String(sale.rentalStartDate || sale.rentStartDate || sale.startDate || ""))
    setRentalEndDate(String(sale.rentalEndDate || sale.rentEndDate || sale.endDate || ""))
    setAvailability(String(sale.availability || "OUI"))
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-xl"
          initial={{ scale: 0.94, y: 40 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.94, y: 40 }}
          transition={{ duration: 0.25 }}
        >
          <div className={`bg-gradient-to-r px-6 py-5 text-white ${heroClass}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px] uppercase tracking-[0.14em]">
                  {propertyTransaction ? <Home size={12} /> : <ShoppingIcon />}
                  {propertyTransaction ? "Transaction immobiliere" : "Detail de vente"}
                </p>
                <h2 className="mt-2 text-lg font-semibold">
                  {propertyTransaction ? "Dossier de vente ou location" : "Detail de la vente"}
                </h2>
                <p className="mt-1 text-sm text-white/85">
                  {propertyTransaction
                    ? "Suivez le client, le montant, la periode d'occupation et la disponibilite du bien."
                    : "Consultez le client, les paiements et l'etat du dossier."}
                </p>
              </div>
              <button onClick={onClose} className="rounded-xl p-2 transition hover:bg-white/20">
                <X />
              </button>
            </div>
          </div>

          <div className="max-h-[80vh] overflow-y-auto p-6 space-y-6">
            <Section
              title={propertyTransaction ? "Bien" : isGeneralSale ? "Produit" : "Telephone"}
              icon={propertyTransaction ? <Home size={18} /> : <Smartphone size={18} />}
              propertyTheme={propertyTransaction}
            >
              {isGeneralSale ? (
                <>
                  <Info label="Nom" value={productName} />
                  <Info label="Reference" value={productRef} />
                  <Info label="Marque" value={productBrand || "-"} />
                  <Info label="Date" value={new Date(sale.soldAt).toLocaleString()} />
                </>
              ) : (
                <>
                  <Info
                    label="Modele"
                    value={`${sale.modelId?.name} (${sale.modelId?.brand})`}
                  />
                  <Info label="IMEI" value={sale.imei} />
                  <Info label="Date" value={new Date(sale.soldAt).toLocaleString()} />
                </>
              )}
            </Section>

            {propertyTransaction && (
              <Section
                title="Transaction"
                icon={<CalendarRange size={18} />}
                propertyTheme
              >
                {editing ? (
                  <>
                    <Field label="Type d'operation">
                      <select
                        value={transactionType}
                        onChange={(e) => setTransactionType(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      >
                        <option value="SALE">vente</option>
                        <option value="RENT">location</option>
                      </select>
                    </Field>
                    <Field label="Disponibilite">
                      <select
                        value={availability}
                        onChange={(e) => setAvailability(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      >
                        <option value="OUI">Oui</option>
                        <option value="NON">Non</option>
                      </select>
                    </Field>
                    <Field label="Nombre de jours">
                      <input
                        type="number"
                        min={1}
                        value={stayDays}
                        onChange={(e) => setStayDays(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Debut location">
                      <input
                        type="date"
                        value={rentalStartDate}
                        onChange={(e) => setRentalStartDate(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                    </Field>
                    <Field label="Fin location">
                      <input
                        type="date"
                        value={rentalEndDate}
                        onChange={(e) => setRentalEndDate(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                    </Field>
                  </>
                ) : (
                  <>
                    <Info label="Type" value={resolveTransactionType(transactionType)} />
                    <Info label="Disponibilite" value={availability === "OUI" ? "Oui" : "Non"} />
                    <Info label="Nombre de jours" value={stayDays || "-"} />
                    <Info label="Periode" value={resolveRentalPeriod(rentalStartDate, rentalEndDate)} />
                    <Info
                      label="Etat"
                      value={sale.remaining > 0 ? "Dossier avec reste" : "Dossier regle"}
                      highlight={sale.remaining > 0 ? "danger" : "success"}
                    />
                  </>
                )}
              </Section>
            )}

            <Section title="Client" icon={<User size={18} />} propertyTheme={propertyTransaction}>
              {editing ? (
                <>
                  <Field label="Nom">
                    <input
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </Field>
                  <Field label="Telephone">
                    <input
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    />
                  </Field>
                </>
              ) : (
                <>
                  <Info label="Nom" value={sale.clientName} />
                  <Info label="Telephone" value={sale.clientPhone || "-"} />
                </>
              )}
            </Section>

            <Section title="Paiement" icon={<DollarSign size={18} />} propertyTheme={propertyTransaction}>
              {editing ? (
                <>
                  {isGeneralSale && (
                    <>
                      {!propertyTransaction && (
                        <Field label="Quantite">
                          <input
                            type="number"
                            min={1}
                            value={quantity}
                            onChange={(e) => setQuantity(e.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                          />
                        </Field>
                      )}
                      <Field label={propertyTransaction && resolveTransactionType(transactionType) === "location" ? "Montant location" : "Prix unitaire"}>
                        <input
                          type="number"
                          min={0}
                          value={unitSellPrice}
                          onChange={(e) => setUnitSellPrice(e.target.value)}
                          className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                        />
                      </Field>
                    </>
                  )}
                  {!isGeneralSale && (
                    <Field label="Prix de vente">
                      <input
                        type="number"
                        min={0}
                        value={phoneSellPrice}
                        onChange={(e) => setPhoneSellPrice(e.target.value)}
                        className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                      />
                    </Field>
                  )}
                  <Field label="Methode">
                    <select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="CASH">Cash</option>
                      <option value="MOBILE_MONEY">Mobile money</option>
                      <option value="DELIVERER">Livreur</option>
                    </select>
                  </Field>
                  <Info
                    label="Total recalcule"
                    value={`${(isGeneralSale ? nextGeneralTotal : nextPhoneTotal).toLocaleString()} FCFA`}
                  />
                </>
              ) : (
                <Info
                  label={propertyTransaction ? "Montant de la transaction" : "Prix de vente"}
                  value={`${sale.sellPrice?.toLocaleString()} FCFA`}
                />
              )}
              <Info label="Montant paye" value={`${sale.paid?.toLocaleString()} FCFA`} />
              <Info
                label="Reste a payer"
                value={
                  (editing ? previewRemaining : sale.remaining) > 0
                    ? `${(editing ? previewRemaining : sale.remaining).toLocaleString()} FCFA`
                    : "0 FCFA"
                }
                highlight={(editing ? previewRemaining : sale.remaining) > 0 ? "danger" : "success"}
              />
              {!editing && <Info label="Methode" value={sale.paymentMethod || "-"} />}
              {!editing && sale.paymentMethod === "MOBILE_MONEY" && (
                <Info
                  label="Lien de paiement"
                  value={sale.payOnline ? "Oui (suivi GESTOCK)" : "Non (personnel)"}
                />
              )}
            </Section>

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock size={16} />
                {propertyTransaction ? "Dossier enregistre" : "Vente enregistree"}
              </div>

              <span
                className={`rounded-full px-3 py-1 text-xs font-medium ${
                  sale.remaining > 0 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"
                }`}
              >
                {sale.remaining > 0 ? "Dette en cours" : "Paye"}
              </span>
            </div>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                <button
                  onClick={() => {
                    if (editing) {
                      resetEditingState()
                      return
                    }
                    setEditing(true)
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-slate-700 transition hover:bg-slate-50"
                >
                  <Pencil size={16} />
                  {editing ? "Annuler" : propertyTransaction ? "Modifier le dossier" : "Modifier la vente"}
                </button>

                {editing && (
                  <button
                    onClick={handleSave}
                    disabled={saving || (isGeneralSale ? nextGeneralTotal < paidAmount : nextPhoneTotal < paidAmount)}
                    className={`inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2 text-white transition disabled:opacity-50 ${accentButtonClass}`}
                  >
                    <Save size={16} />
                    {saving ? "Enregistrement..." : "Enregistrer"}
                  </button>
                )}

                {isAdmin ? (
                  <button
                    onClick={() => setConfirmDeleteOpen(true)}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 px-4 py-2 text-red-600 transition hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                    {propertyTransaction ? "Supprimer le dossier" : "Supprimer la vente"}
                  </button>
                ) : (
                  <p className="text-xs text-slate-500">
                    Seul un administrateur peut supprimer une vente.
                  </p>
                )}
              </div>

              {sale.remaining > 0 && (
                <button
                  onClick={() => setIsPaymentOpen(true)}
                  className="rounded-xl bg-green-600 px-4 py-2 text-white transition hover:bg-green-700"
                >
                  Encaisser un paiement
                </button>
              )}
            </div>

            <button
              onClick={() =>
                window.open(
                  isGeneralSale
                    ? `${BASE_URL}/api/gestock/general/invoice/${sale._id}/${storeId}`
                    : `${BASE_URL}/api/gestock/invoice/${sale._id}/${storeId}`,
                  "_blank"
                )
              }
              className="rounded-xl border px-4 py-2"
            >
              Telecharger la facture
            </button>
          </div>
        </motion.div>
      </motion.div>

      {isPaymentOpen && (
        <CollectPaymentModal
          isOpen={isPaymentOpen}
          storeId={storeId}
          sale={sale}
          onClose={() => setIsPaymentOpen(false)}
          onConfirm={(updatedSale) => {
            onUpdate(updatedSale)
            setIsPaymentOpen(false)
          }}
        />
      )}

      {confirmDeleteOpen && (
        <ConfirmDeleteModal
          sale={sale}
          deleting={deleting}
          onCancel={() => {
            if (!deleting) setConfirmDeleteOpen(false)
          }}
          onConfirm={handleDelete}
          propertyTransaction={propertyTransaction}
        />
      )}
    </AnimatePresence>
  )
}

function Section({
  title,
  icon,
  children,
  propertyTheme = false,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  propertyTheme?: boolean
}) {
  return (
    <div className={`rounded-2xl border p-4 space-y-3 ${propertyTheme ? "border-emerald-100 bg-emerald-50/40" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
        {icon}
        {title}
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm">{children}</div>
    </div>
  )
}

function Info({
  label,
  value,
  highlight,
}: {
  label: string
  value: string
  highlight?: "danger" | "success"
}) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p
        className={`font-medium ${
          highlight === "danger"
            ? "text-red-600"
            : highlight === "success"
              ? "text-green-600"
              : "text-gray-800"
        }`}
      >
        {value}
      </p>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      {children}
    </div>
  )
}

function ConfirmDeleteModal({
  sale,
  deleting,
  onCancel,
  onConfirm,
  propertyTransaction,
}: {
  sale: any
  deleting: boolean
  onCancel: () => void
  onConfirm: () => void
  propertyTransaction: boolean
}) {
  return (
    <motion.div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl"
        initial={{ scale: 0.94, y: 24 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 24 }}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
            <AlertTriangle size={18} />
          </div>
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-slate-900">
              {propertyTransaction ? "Supprimer ce dossier ?" : "Supprimer cette vente ?"}
            </h3>
            <p className="text-sm text-slate-600">
              Cette action va annuler la vente de{" "}
              <strong>{sale?.productId?.name || sale?.productName || sale?.modelId?.name || "cet article"}</strong>.
            </p>
            <p className="text-sm text-slate-600">
              Le stock sera restaure automatiquement et cette action est reservee aux administrateurs.
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={deleting}
            className="rounded-xl border border-slate-200 px-4 py-2 text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="rounded-xl bg-red-600 px-4 py-2 text-white transition hover:bg-red-700 disabled:opacity-50"
          >
            {deleting ? "Suppression..." : "Supprimer"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  )
}

function resolveTransactionType(type: string) {
  const normalized = String(type || "SALE").toUpperCase()

  if (normalized === "RENT") return "location"
  return "vente"
}

function resolveRentalPeriod(startDate: string, endDate: string) {
  if (!startDate && !endDate) return "-"

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    })

  return `${startDate ? formatDate(startDate) : "-"} -> ${endDate ? formatDate(endDate) : "-"}`
}

function ShoppingIcon() {
  return <DollarSign size={12} />
}
